import SuperSimpleStateSystem from "../ssss";
import d from "../debug";
import { initializePlayer } from "./spotify";
import { initialize } from "./youtube";
import asHTML from "../template";
import {
  DEFAULT_ZOOM_IDX,
  clamp,
  includes,
  wrap,
  sortMarkers,
  getBreakpoints,
  throttle,
  isApprox,
  idxToZoom,
  ZOOM_MAX,
  size,
} from "./lib";

import type { SSSS } from "../ssss";
import type {
  Player,
  AppState,
  Marker,
  Section,
  ActiveSection,
  Updates,
  EditState,
  TrackId,
  Interval,
} from "./lib";
import { deserialize, initLoadState, initState, read, save } from "./storage";

type Mode = "spotify" | "youtube";
const MODE: Mode = "youtube" as Mode; // ????
const PADDING = 24;

declare global {
  interface Window {
    s: SSSS<AppState>;
  }
}

const el = {
  playPause: document.querySelector(".track-play-pause") as HTMLButtonElement,
  prevTrack: document.querySelector(".track-prev") as HTMLButtonElement,
  nextTrack: document.querySelector(".track-next") as HTMLButtonElement,
  seekForward: document.querySelector(
    ".track-seek-forwards",
  ) as HTMLButtonElement,
  seekBack: document.querySelector(".track-seek-back") as HTMLButtonElement,
  scrubber: document.getElementById("scrubber") as HTMLInputElement,
  overview: {
    container: document.getElementById("overview") as HTMLDivElement,
    playhead: document.getElementById("overview-playhead") as HTMLDivElement,
    progress: document.getElementById("overview-progress") as HTMLDivElement,
  },
  editorMarkers: document.getElementById("editor-markers") as HTMLDivElement,
  overviewMarkers: document.getElementById(
    "overview-markers",
  ) as HTMLDivElement,
  overviewBar: document.getElementById("overview-bar") as HTMLDivElement,
  playhead: document.getElementById("playhead") as HTMLDivElement,
  playheadHandle: document.getElementById("playhead-handle") as HTMLDivElement,
  editorSection: document.getElementById("editor-section") as HTMLDivElement,
  editor: document.getElementById("editor") as HTMLDivElement,
  squiggle: document.getElementById("squiggle") as HTMLDivElement,
  overviewWindow: document.getElementById("overview-window") as HTMLDivElement,
  trackIdInput: document.getElementById("track-id") as HTMLInputElement,
  trackIdSubmit: document.getElementById("track-submit") as HTMLButtonElement,
  zoom: {
    amt: document.getElementById("zoom-amt") as HTMLSpanElement,
    in: document.getElementById("zoom-in") as HTMLButtonElement,
    out: document.getElementById("zoom-out") as HTMLButtonElement,
  },
  prevSection: document.getElementById("section-prev") as HTMLButtonElement,
  nextSection: document.getElementById("section-next") as HTMLButtonElement,
  addMarker: document.getElementById("marker-add") as HTMLButtonElement,
  restartSection: document.getElementById(
    "section-restart",
  ) as HTMLButtonElement,
  loopToggle: document.getElementById("loop") as HTMLButtonElement,
  loopStatus: document.getElementById("loop-status") as HTMLSpanElement,
  undoBtn: document.getElementById("undo-btn") as HTMLButtonElement,
  redoBtn: document.getElementById("redo-btn") as HTMLButtonElement,
  prevMarker: document.getElementById("marker-prev") as HTMLButtonElement,
  nextMarker: document.getElementById("marker-next") as HTMLButtonElement,
  delMarker: document.getElementById("marker-del") as HTMLButtonElement,
  toggleFollow: document.getElementById("toggle-follow") as HTMLButtonElement,
  followStatus: document.getElementById("follow-status") as HTMLSpanElement,
  speedContainer: document.getElementById("speed-container") as HTMLDivElement,
};

//////////////
/// state ///
////////////

const initializeState = () => {
  return SuperSimpleStateSystem({ ...initState });
};

const mkStateInterface = (s4: SSSS<AppState>, player: Player) => {
  window.s = s4;

  s4.sub(["history", "historyIdx"], () => {
    d(100, "history changed: restore markers");
    const { history, historyIdx } = s4.get();
    s4.mod("markers", history[historyIdx].markers);
  });

  function addHistoryItem(item: EditState) {
    const { history, historyIdx } = s4.get();
    let newHistory = [...history];
    if (historyIdx < history.length - 1) {
      newHistory = newHistory.slice(0, historyIdx + 1);
    }
    newHistory.push(item);
    return {
      history: newHistory,
      historyIdx: historyIdx + 1,
    };
  }

  const userSeek = async (pos: number) => {
    player.unmute(); // TODO tmp till delay state. in case we're in delay mode
    const clampedPos = clamp(pos, { start: 0, end: s4.get("duration") });
    s4.update({ activeSectionIdx: sectionAtPosition(clampedPos) });
    await seek(pos);
    return;
  };

  const seek = async (pos: number) => {
    const clampedPos = clamp(pos, { start: 0, end: s4.get("duration") });
    await player.seek(clampedPos);
    return;
  };

  const seekRelative = async (delta: number) => {
    await userSeek(s4.get("position") + delta);
  };

  const dynamicSeek = (
    basis: number,
    scale: number = 1.2,
    maxScale: number = 6,
    timeout: number = 750,
  ) => {
    let multiplier = 1;
    // see TSS
    let to: number | undefined | any;
    return () => {
      clearTimeout(to);
      const delta = basis * multiplier;
      seekRelative(delta);
      multiplier = Math.min(maxScale, multiplier * scale);
      to = setTimeout(() => {
        multiplier = 1;
      }, timeout);
      return delta;
    };
  };

  // markers: the saved ones
  // editingMarkers: the ones being edited through dragging. not yet commited.
  // activeMarker: editingMarker if exists else marker.
  // same goes for section since that's derived from markers.
  function getActiveMarkers(): Marker[] {
    const { markers, editingMarkers } = s4.get();
    return editingMarkers || markers;
  }

  function getSection(markers: Marker[] = s4.get("markers")): Section {
    const { activeSectionIdx, duration } = s4.get();
    const bps = getBreakpoints(markers, duration);
    return { start: bps[activeSectionIdx], end: bps[activeSectionIdx + 1] };
  }

  function getActiveSection(): Section {
    return getSection(getActiveMarkers());
  }

  function setupNewTrackState(trackId: TrackId) {
    const storedTrack = read(trackId);
    if (storedTrack) {
      d(0, "got stored track", storedTrack);
      s4.update({ ...initLoadState, ...deserialize(storedTrack), trackId });
    } else {
      d(0, "new track. initializing...");
      s4.update({ ...initLoadState, trackId });
    }
  }

  async function validate(input: string) {
    return player.validate(input);
  }

  async function load(trackId: TrackId) {
    d(0, "load", trackId);
    player?.load(trackId);
    setupNewTrackState(trackId);
    player?.pause();
  }

  function incSectionIdx(delta: number = 1) {
    const { activeSectionIdx, markers } = s4.get();
    return s4.update({
      activeSectionIdx: wrap(activeSectionIdx + delta, {
        start: 0,
        end: markers.length + 1,
      }),
    });
  }

  function sectionAtPosition(
    position: number = s4.get("position"),
    markers: Marker[] = s4.get("markers"),
  ) {
    // looking for the closest marker before the given position.
    const reversedIdx = markers
      .toReversed()
      .findIndex(({ pos }) => position >= pos);
    return reversedIdx < 0 ? 0 : markers.length - reversedIdx;
  }

  return {
    ...s4,
    toggleMode: () => {
      const { mode } = s4.get();
      s4.mod(
        "mode",
        mode === "once"
          ? "through"
          : mode === "through"
            ? "delay"
            : mode === "delay"
              ? "loop"
              : "once",
      );
    },
    seek: userSeek,
    autoSeek: seek,
    seekRelative,
    dynamicSeek,
    nextTrack: player.next,
    prevTrack: player.prev,
    pause: player.pause,
    togglePlay: player.togglePlay,
    mute: player.mute,
    unmute: player.unmute,
    getSection,
    getActiveSection,
    getActiveMarkers,
    addHistoryItem,
    load,
    validate,
    incSectionIdx,
    sectionAtPosition,
    renderedPosition: () => {
      const { scrubbing, position } = s4.get();
      return scrubbing === false ? position : scrubbing;
    },
    zoom: {
      in: () => {
        s4.mod("zoomIdx", Math.min(ZOOM_MAX, s4.get("zoomIdx") + 1));
      },
      out: () => {
        s4.mod("zoomIdx", Math.max(0, s4.get("zoomIdx") - 1));
      },
    },
  };
};

type StateInterface = ReturnType<typeof mkStateInterface>;

///////////////
/// player ///
/////////////

const setupControls = (s: StateInterface) => {
  type Action = {
    el: HTMLElement;
    key: string | ((e: KeyboardEvent) => boolean);
    fn: null | (() => any);
  };

  const playAction = {
    el: el.playPause,
    key: " ",
    fn: () => {
      d(3, "togglePlay", s.get("position"));
      s.togglePlay();
    },
  };

  const playerActions: Action[] = [
    {
      el: el.prevTrack,
      key: "z",
      fn: s.prevTrack,
    },
    {
      el: el.nextTrack,
      key: "b",
      fn: s.nextTrack,
    },
  ];

  const controls: Action[] = [
    {
      el: el.toggleFollow,
      key: "o",
      fn: () => {
        s.update({ following: !s.get("following") });
      },
    },
    {
      el: el.zoom.in,
      key: (e) => e.key === "+" || e.key === "=",
      fn: s.zoom.in,
    },
    {
      el: el.zoom.out,
      key: (e) => e.key === "-" || e.key === "_",
      fn: s.zoom.out,
    },
    {
      el: el.prevSection,
      key: "p",
      fn: () => {
        s.incSectionIdx(-1);
        s.seek(s.getActiveSection().start);
      },
    },
    {
      el: el.nextSection,
      key: "n",
      fn: () => {
        s.incSectionIdx(1);
        s.seek(s.getActiveSection().start);
      },
    },
    {
      el: el.prevMarker,
      key: "b",
      fn: () => s.mod("activeMarkerIdx", incMarkerIdx(-1)),
    },
    {
      el: el.nextMarker,
      key: "f",
      fn: () => s.mod("activeMarkerIdx", incMarkerIdx(1)),
    },
    {
      el: el.delMarker,
      key: "d",
      fn: () => {
        const { activeSectionIdx, activeMarkerIdx, markers } = s.get();
        if (typeof activeMarkerIdx === "number") {
          const newMarkers = [...markers];
          newMarkers.splice(activeMarkerIdx, 1);
          // if the deleted marker is to the left of the active section, the section idx will need to decrement.
          const updates: Updates = {
            markers: newMarkers,
            activeMarkerIdx: null,
          };
          if (activeMarkerIdx < activeSectionIdx) {
            updates.activeSectionIdx = activeSectionIdx - 1;
          }
          s.update(updates);
        }
      },
    },
    {
      el: el.restartSection,
      key: "g",
      fn: () => s.seek(s.getActiveSection().start),
    },
    {
      el: el.addMarker,
      key: "t",
      fn: () => {
        const thresh = 20;
        const { markers, position, activeSectionIdx } = s.get();
        for (let m of markers) {
          if (Math.abs(position - m.pos) < thresh) {
            d(4, "skipping marker add - too close to another marker.");
            return;
          }
        }
        const newMarkers = [...markers];
        newMarkers.push({ pos: position });
        newMarkers.sort(sortMarkers);
        const updates: Updates = {
          ...s.addHistoryItem({ markers: newMarkers }),
        };
        const section = s.getActiveSection();
        if (includes(position, section)) {
          updates.activeSectionIdx = activeSectionIdx + 1;
        }
        s.update(updates);
      },
    },
    {
      el: el.loopToggle,
      key: "l",
      fn: s.toggleMode,
    },
    {
      el: el.undoBtn,
      key: "u",
      fn: () => {
        const { historyIdx } = s.get();
        if (historyIdx > 0) {
          s.update({
            historyIdx: historyIdx - 1,
          });
        }
      },
    },
    {
      el: el.redoBtn,
      key: "r",
      fn: () => {
        const { historyIdx, history } = s.get();
        if (historyIdx < history.length - 1) {
          s.update({
            historyIdx: historyIdx + 1,
          });
        }
      },
    },
  ];

  const dynamicActions: Action[] = [
    {
      el: el.seekBack,
      key: "x",
      fn: s.dynamicSeek(-5 * 1000),
    },
    {
      el: el.seekForward,
      key: "v",
      fn: s.dynamicSeek(5 * 1000),
    },
  ];

  const allActions = [
    playAction,
    ...controls,
    ...playerActions,
    ...dynamicActions,
  ];

  for (let action of [playAction, ...playerActions, ...controls]) {
    if (action.fn) {
      action.el.addEventListener("click", action.fn);
    } else {
      if ("disabled" in action.el) {
        action.el.disabled = true;
      }
    }
  }

  for (let action of dynamicActions) {
    // TSS: any because my editor doesn't support using a tsserver from
    // the current project right now. so nodejs types are used and errored upon.
    let interval: number | undefined | any;
    let timeout: number | undefined | any;

    const onUp = () => {
      clearInterval(interval);
      clearTimeout(timeout);
    };

    action.el.addEventListener("mousedown", () => {
      if (action.fn) {
        action.fn();
        timeout = setTimeout(() => {
          if (action.fn) {
            interval = setInterval(action.fn, 100);
          }
        }, 250);
      }
    });

    action.el.addEventListener("mouseup", onUp);
  }

  function markersSurroundingSection(sectionIdx: number) {
    return {
      left: sectionIdx > 0 ? sectionIdx - 1 : null,
      right: sectionIdx < s.get("markers").length ? sectionIdx : null,
    };
  }

  function incMarkerIdx(delta: number = 1) {
    const { activeMarkerIdx, activeSectionIdx, markers } = s.get();
    let curMarkerIdx = 0;

    if (typeof activeMarkerIdx === "number") {
      curMarkerIdx = activeMarkerIdx;
    } else {
      // technically we're between markers, so +1 gets us to the right marker, and -1 gets us to the left one.
      const { left, right } = markersSurroundingSection(activeSectionIdx);
      curMarkerIdx = (delta >= 0 ? left : right) ?? 0;
    }
    return wrap(curMarkerIdx + delta, { start: 0, end: markers.length });
  }

  document.body.addEventListener("keydown", (e) => {
    // TODO: handling track error like this at the root works, but it may be better to explicitly handle the failure case in each action. i bet some things should be allowed.
    if (s.get("trackError")) return;
    switch (e.key) {
      case "Escape": {
        if (typeof s.get("activeMarkerIdx") === "number") {
          s.mod("activeMarkerIdx", null);
        }
        break;
      }
      default: {
        const action = allActions.find((pa) =>
          typeof pa.key === "function" ? pa.key(e) : pa.key === e.key,
        );
        if (action && action.fn) {
          e.preventDefault();
          e.stopPropagation();
          action.fn();
        }
        break;
      }
    }
  });
};

/////////////////////
/// Song-Learner ///
///////////////////

document.addEventListener("DOMContentLoaded", async () => {
  const _s4 = initializeState();
  const player =
    MODE === "youtube"
      ? await initialize(_s4.get("trackId"), el.speedContainer)
      : await initializePlayer();
  if (!player) return;
  const s = mkStateInterface(_s4, player);

  player.onStateChange(s.update);

  setupControls(s);

  const updateFollowing = () => {
    el.followStatus.innerHTML = s.get("following") ? "✅" : "❌";
  };
  updateFollowing();
  s.sub("following", updateFollowing);

  const updateLooping = () => {
    el.loopStatus.innerHTML = s.get("mode");
  };
  updateLooping();
  s.sub("mode", updateLooping);

  s.sub(["position", "markers", "editingMarkers"], () => {
    s.update({
      currentSectionIdx: s.sectionAtPosition(
        s.get("position"),
        s.getActiveMarkers(),
      ),
    });
  });

  const handleMode = async () => {
    const {
      mode,
      muted,
      currentSectionIdx: cur,
      activeSectionIdx: active,
    } = s.get();
    const section = s.getActiveSection();
    switch (mode) {
      case "loop": {
        if (cur !== active) {
          s.autoSeek(section.start);
        }
        break;
      }
      case "once": {
        if (cur !== active) {
          // cur < active means the marker was dragged across the
          // playhead. don't pause in this case, as that's annoying.
          if (cur > active) {
            s.pause();
          }
          await s.autoSeek(section.start);
        }
        break;
      }
      case "delay": {
        if (cur > active) {
          if (muted) {
            s.unmute();
          } else {
            s.mute();
          }
        }
        if (cur !== active) {
          await s.autoSeek(section.start);
        }
        break;
      }
      case "through": {
        s.update({ activeSectionIdx: cur });
        break;
      }
    }
  };

  s.sub("currentSectionIdx", handleMode);

  // there's a case where currentSectionIdx won't change after an autoseek: the marker has already moved in such a way that the destination of the seek is in the same section. to cover this case, we must do another autoseek. throttled to prevent glitchy audio from spamming seek.

  // the behavior here is subtly different here
  s.sub(["markers", "editingMarkers"], throttle(handleMode, 100));

  s.sub(["activeSectionIdx"], () => {
    // TODO: handle user mutes... will happen naturally when there's state to manage the delay number of loops...
    s.unmute();
  });

  // storage
  s.sub(["history", "historyIdx"], () => {
    d(100, "save to localStorage");
    save(s.get());
  });

  function renderZoomControl(zoom: number) {
    el.zoom.amt.innerHTML = String("x" + zoom);
  }
  renderZoomControl(idxToZoom(s.get("zoomIdx")));

  s.sub(["zoomIdx"], ({ zoomIdx: oldZoomIdx }) => {
    const newZoom = idxToZoom(s.get("zoomIdx"));
    const zoom = idxToZoom(oldZoomIdx);
    renderZoomControl(newZoom);

    d(100, "set zoom state");
    if (!s.get("following") && s.get("centerProgress") == null) {
      d(0, "setting up center progress");
      // set up a temporary center point if not following. this way, zooms that
      // hit a side don't lose their center. also used when animating the zoom, so that the center stays in the center.
      const sectionBounds = el.editorSection.getBoundingClientRect();
      const center =
        el.editorSection.scrollLeft - PADDING + sectionBounds.width / 2;
      const scrollMax = el.editorSection.scrollWidth - sectionBounds.width;
      let focalPoint = center;
      if (zoom > 1) {
        if (el.editorSection.scrollLeft === 0) {
          focalPoint = 0;
        } else if (el.editorSection.scrollLeft === scrollMax) {
          focalPoint = scrollMax;
        }
      }
      const zoomedEditorWidth = el.editor.getBoundingClientRect().width;
      // percentage of the way through the track at the center of the view.
      const progress = focalPoint / zoomedEditorWidth;
      s.update({ centerProgress: progress });
    }
    zoomOverTime(100, zoom, newZoom);
  });

  const zoomOverTime = (ms: number, start: number, end: number) => {
    let t: undefined | DOMHighResTimeStamp = undefined;
    const doIt = (ts: DOMHighResTimeStamp) => {
      if (t === undefined) {
        t = ts;
        requestAnimationFrame(doIt);
      } else {
        const elapsed = ts - t;
        let zoomAmt = (end - start) * (elapsed / ms) + start;
        if (start > end) {
          zoomAmt = Math.max(zoomAmt, end);
        } else {
          zoomAmt = Math.min(zoomAmt, end);
        }
        s.update({ zoom: zoomAmt });

        if (elapsed < ms) {
          requestAnimationFrame(doIt);
        }
      }
    };
    requestAnimationFrame(doIt);
  };

  s.sub(
    ["position", "duration", "scrubbing", "following", "zoom"],
    (prevState) => {
      const { following, zoom, centerProgress } = s.get();
      if (following) {
        d(100, "following playhead: centering.");
        const { position, duration, scrubbing } = s.get();
        const pos = typeof scrubbing === "number" ? scrubbing : position;
        centerEditor(zoom, pos / duration);
      } else if (prevState.zoom !== zoom && centerProgress != null) {
        d(100, "not following playhead: centering.");
        centerEditor(zoom, centerProgress);
      }
    },
  );

  s.sub(["activeMarkerIdx"], () => {
    d(100, "activate marker style");
    const { activeMarkerIdx } = s.get();
    activateMarker(activeMarkerIdx);
  });

  const timeline = Timeline();

  timeline.renderWindow(s.get("zoom"));

  timeline.addScrubListener({
    updateScrubbingPreview: (scrubbing: number | false) => {
      d(1000, "update scrubbing position");
      s.update({ scrubbing });
    },
    onSeek: async (position: number) => {
      d(2, "seeking to ", position);
      await s.seek(position);
      d(4, "seeked to ", position);
    },
  });

  const renderTimeline = timeline.renderTimeline;

  s.sub(["position", "duration"], () => {
    const { position, duration } = s.get();
    d(100, "render timeline position");
    if (s.get("scrubbing") === false) {
      renderTimeline(s.get("position"), s.get("duration"));
      timeline.updateRangeInput(position, duration);
    }
  });

  s.sub(["zoom"], () => {
    timeline.renderWindow(s.get("zoom"));
  });

  el.editorSection.addEventListener("scroll", (evt) => {
    d(
      99,
      "scroll! rendering lol. i need to only do this when manually scrolling...",
    );
    // if scroll is way off what we update it to, we know it was done by a user.
    if (s.get("following")) {
      if (
        !isApprox(
          el.editorSection.scrollLeft,
          calculateView(s.get("zoom"), s.renderedPosition() / s.get("duration"))
            .scrollLeft,
          5,
        )
      ) {
        s.update({ following: false });
      }
    } else {
      const { centerProgress } = s.get();
      if (
        centerProgress != null &&
        !isApprox(
          el.editorSection.scrollLeft,
          calculateView(s.get("zoom"), centerProgress).scrollLeft,
          5,
        )
      ) {
        s.update({ centerProgress: null });
      }
    }
    timeline.renderWindow(s.get("zoom"));
  });

  function shiftMarker(markers: Marker[], idx: number, delta: number) {
    const updatedMarkers = [...markers];
    updatedMarkers[idx] = { ...updatedMarkers[idx] };
    updatedMarkers[idx].pos = clamp(updatedMarkers[idx].pos + delta, {
      start: 0,
      end: s.get("duration"),
    });
    updatedMarkers.sort((a, b) => a.pos - b.pos);
    return updatedMarkers;
  }

  function xToPosition(x: number, el: HTMLElement) {
    const bounds = el.getBoundingClientRect();
    const progress = x / bounds.width;
    const position = progress * s.get("duration");
    return clamp(position, { start: 0, end: s.get("duration") });
  }

  const editor = Editor();
  // set up playhead drag handle
  addDragHandlers(
    el.playheadHandle,
    el.editorSection,
    (deltaX, startX, startScroll) => {
      // screen x to position...
      s.update({
        scrubbing: xToPosition(
          startX + startScroll + deltaX - PADDING,
          el.editor,
        ),
      });
    },
    async (deltaX, startX, startScroll) => {
      await s.seek(
        xToPosition(startX + startScroll + deltaX - PADDING, el.editor),
      );
      s.update({
        scrubbing: false,
      });
    },
    () => {
      s.update({ following: false });
    },
  );

  s.sub(["editingMarkers", "markers", "duration"], () => {
    d(100, "render markers");
    const {
      activeSectionIdx,
      editingMarkers,
      markers,
      duration,
      activeMarkerIdx,
    } = s.get();
    editor.renderMarkers({
      activeSectionIdx: activeSectionIdx,
      markers: editingMarkers || markers,
      duration: duration,
      updateMarkerPreview: (idx: number, delta: number) => {
        s.mod("activeMarkerIdx", idx);
        s.updateViaProxy((st) => {
          st.editingMarkers = shiftMarker(markers, idx, delta);
        });
      },
      finalizeMarker: (idx: number, delta: number) => {
        s.update({
          activeMarkerIdx: null,
          editingMarkers: null,
          ...s.addHistoryItem({ markers: shiftMarker(markers, idx, delta) }),
        });
      },
      activateSection: (sectionIdx: number) => {
        s.mod("activeSectionIdx", sectionIdx);
        s.seek(s.getActiveSection().start);
      },
    });
    activateMarker(activeMarkerIdx);
  });

  s.sub("activeSectionIdx", () => {
    d(100, "update active section html");
    editor.updateActive(s.get("activeSectionIdx"));
  });

  s.sub(["duration", "position", "scrubbing"], () => {
    d(100, "render track position");
    const scrubbing = s.get("scrubbing");
    const positionToRender = s.renderedPosition();
    d(1000, "scrubbing", scrubbing, ", positionToRender", positionToRender);

    if (!scrubbing) {
      timeline.updateRangeInput(positionToRender, s.get("duration"));
    }
    renderTimeline(positionToRender, s.get("duration"));
    editor.renderTrackPosition({
      position: positionToRender,
      duration: s.get("duration"),
    });
  });

  TrackSelect({
    onSubmit: s.load,
    validate: s.validate,
  });

  // finally, load the track here so subscriptions can run.
  s.load(s.get("trackId"));
});

///////////////////
/// Components ///
/////////////////

function addDragHandlers(
  el: HTMLElement,
  scrollParent: HTMLElement,
  onUpdate: (deltaPx: number, startX: number, startScroll: number) => void,
  onFinalize: (deltaPx: number, startX: number, startScroll: number) => void,
  onStart?: () => void,
) {
  let scrollAmt = 0;
  let startX: number = 0;
  let curX: number = 0;
  let startScroll: number = scrollParent.scrollLeft;
  let _doloop = false;

  function getScroll() {
    return (scrollParent?.scrollLeft || 0) - startScroll;
  }
  function calcDelta() {
    return curX - startX + getScroll();
  }
  function doUpdate() {
    onUpdate(calcDelta(), startX, startScroll);
  }
  const SCROLL_SCALE = 50;
  function calcLeftScrollAmt(mouseX: number, left: number, margin: number) {
    const distanceFromMargin = Math.min(
      1,
      (left + PADDING + margin - mouseX) / (margin + PADDING),
    );
    return -1 * SCROLL_SCALE * Math.pow(distanceFromMargin, 2);
  }
  function calcRightScrollAmt(mouseX: number, right: number, margin: number) {
    const distanceFromMargin = Math.min(
      1,
      (mouseX - (right - PADDING) + margin) / (margin + PADDING),
    );
    return SCROLL_SCALE * Math.pow(distanceFromMargin, 2);
  }

  function animationLoop() {
    if (_doloop) {
      scrollParent.scrollLeft = scrollParent.scrollLeft + scrollAmt;
      doUpdate();
    }
    if (_doloop) {
      requestAnimationFrame(animationLoop);
    }
  }

  const onMove = (e: MouseEvent) => {
    curX = e.clientX;
    // maybe scroll?
    const bounds = scrollParent.getBoundingClientRect();
    const MARGIN = Math.max(bounds.width / 20, 100); // 5% of the width on either side for scroll.
    if (curX < bounds.left + MARGIN) {
      scrollAmt = calcLeftScrollAmt(e.clientX, bounds.left, MARGIN);
    } else if (e.clientX > bounds.right - MARGIN) {
      scrollAmt = calcRightScrollAmt(e.clientX, bounds.right, MARGIN);
    } else {
      scrollAmt = 0;
    }
  };

  const onUp = (e: MouseEvent) => {
    curX = e.clientX;
    _doloop = false;
    document.body.classList.remove("dragging");
    document.removeEventListener("mousemove", onMove);
    const finalDelta = calcDelta();
    if (finalDelta === 0) return;
    onFinalize(finalDelta, startX, startScroll);
  };

  el.style.cursor = "grab";

  el.addEventListener("mousedown", (e: MouseEvent) => {
    startX = curX = e.clientX;
    startScroll = scrollParent.scrollLeft;
    _doloop = true;
    requestAnimationFrame(animationLoop);
    document.body.classList.add("dragging");
    document.addEventListener("mousemove", onMove);
    document.addEventListener("mouseup", onUp, { once: true });
    onStart && onStart();
  });
}

function Timeline() {
  const { scrubber, overviewWindow, editorSection, editor } = el;

  const updateRangeInput = (position: number, duration: number) => {
    scrubber.value = String(position);
    scrubber.max = String(duration);
  };

  const renderWindow = (zoom: number) => {
    if (zoom === 1) {
      overviewWindow.style.opacity = "0";
    } else {
      overviewWindow.style.opacity = "1";
    }
    const bounds = el.editor.getBoundingClientRect();
    const w = toPercent(1, zoom);
    const halfW = w / 2;
    const scrollPercent = toPercent(el.editorSection.scrollLeft, bounds.width);
    // we want to animate the window change from 3 different points.
    d(4, { w, halfW, scrollPercent });
    // case 1: window hugs the left: scale from left side.
    if (scrollPercent === 0) {
      overviewWindow.style.transform = "translateX(0)";
      overviewWindow.style.left = "0";
      overviewWindow.style.right = "";
      // case 2: window hugs the right: scale from right side.
    } else if (isApprox(scrollPercent + w, 100, 0.01)) {
      overviewWindow.style.transform = "translateX(0)";
      overviewWindow.style.left = "";
      overviewWindow.style.right = "0";
      // case 3: window is somewhere in the middle: scale from the middle of the window.
    } else {
      overviewWindow.style.transform = "translateX(-50%)";
      overviewWindow.style.left = `${scrollPercent + halfW}%`;
      overviewWindow.style.right = "";
    }

    overviewWindow.style.width = `${w}%`;
  };

  const renderTimeline = (position: number, duration: number) => {
    d(4, "rendering timeline!!");
    const progressPercent = (position / duration) * 100;
    const inverseRatio = 100 - progressPercent;
    el.overview.playhead.style.left = `${progressPercent}%`;
  };

  function addScrubListener(props: {
    updateScrubbingPreview: (scrubbing: number | false) => void;
    onSeek: (position: number) => Promise<void>;
  }) {
    const { updateScrubbingPreview, onSeek } = props;
    const onMouseDown = () => {
      let isScrubbing = true;
      const updateLoop = () => {
        if (isScrubbing) {
          updateScrubbingPreview(Number(scrubber.value));
          requestAnimationFrame(updateLoop);
        }
      };
      requestAnimationFrame(updateLoop);
      scrubber.addEventListener(
        "change",
        async () => {
          isScrubbing = false;
          d(4, "onseek");
          await onSeek(Number(scrubber.value));
          d(4, "unsetting scrubbing");
          updateScrubbingPreview(false);
        },
        { once: true },
      );
    };
    scrubber.addEventListener("mousedown", onMouseDown);

    // NOTE: if we want to remove the event on deconstruction:
    // return () => el.removeEventListener("mousedown", onMouseDown);
  }

  return {
    addScrubListener,
    renderTimeline,
    renderWindow,
    updateRangeInput,
  };
}

function toPercent(num: number, total: number): number {
  return (num * 100) / total;
}
function toPercentString(num: number, total: number = 1): string {
  return `${toPercent(num, total)}%`;
}

function calculateView(zoom: number, progress: number) {
  // in px to avoid weird rendering issues.
  const bounds = {
    section: el.editorSection.getBoundingClientRect(),
  };

  const P = PADDING;
  const W = bounds.section.width;
  // Eb: base editor width
  const Eb = W - 2 * P;
  // Es: scaled editor width
  const Es = Eb * zoom;
  // X: px from left to progress point (context is editor)
  const X = progress * Es;
  // SLMax: max scrollLeft
  const SLMax = Math.round(Es + 2 * P - W);

  // constrain the scrollLeft
  const SL = clamp(Math.round(X + P - W / 2), { start: 0, end: SLMax });
  //const SL = X + P <= W / 2 ? 0 : P + Es - X <= W / 2 ? SLMax : X + P - W / 2;
  return {
    width: Math.round(Es),
    scrollLeft: SL,
  };
}

function centerEditor(zoom: number, progress: number) {
  const { scrollLeft, width } = calculateView(zoom, progress);
  // ATTENTION: ORDER IMPORTANT. setting scrollLeft above max will set it to max, so you need enough width to handle the scroll first.
  el.editor.style.width = `${width}px`;
  el.editorSection.scrollLeft = scrollLeft;
}

function Editor() {
  const markerTemplateEl = asHTML<HTMLDivElement>`
<div class="marker">
  <div class="marker-handle teardrop"></div>
</div>`;
  const sectionTemplateEl = asHTML<HTMLDivElement>`<div class="between"></div>`;

  function makeSection(
    start: number,
    end: number,
    duration: number,
    id: number,
    isActive: boolean,
    activate: () => void,
  ) {
    const el = sectionTemplateEl.cloneNode(true) as HTMLElement;
    if (isActive) {
      el.classList.add("active");
    }
    el.style.left = toPercentString(start, duration);
    el.style.right = toPercentString(duration - end, duration);
    el.dataset.idx = String(id);
    el.addEventListener("click", () => {
      console.log("CLICKED");
      activate();
    });
    return el;
  }

  function makeOverviewSection(props: {
    progressRegion: Interval;
    isActive: boolean;
    idx: number;
  }) {
    const s = asHTML`<div class="between" data-idx=${props.idx}></div>`;
    s.style.left = toPercentString(props.progressRegion.start);
    s.style.right = toPercentString(props.progressRegion.end);
    if (props.isActive) {
      s.classList.add("active");
    }
    return s;
  }

  function renderMarkers(props: {
    activeSectionIdx: ActiveSection;
    markers: Marker[];
    duration: number;
    updateMarkerPreview: (markerIdx: number, positionDelta: number) => void;
    finalizeMarker: (markerIdx: number, positionDelta: number) => void;
    activateSection: (sectionIdx: number) => void;
  }) {
    const {
      activeSectionIdx,
      markers,
      duration,
      updateMarkerPreview,
      finalizeMarker,
      activateSection,
    } = props;
    const isActive = (idx: number) => activeSectionIdx === idx;
    el.overviewBar.replaceChildren(
      ...markers.reduce<Node[]>((result, bp, idx) => {
        if (idx === 0) {
          result.push(
            makeOverviewSection({
              progressRegion: { start: 0, end: 1 - bp.pos / props.duration },
              isActive: isActive(idx),
              idx,
            }),
          );
        }

        const nextBpPos =
          1 -
          (idx === markers.length - 1 ? duration : markers[idx + 1].pos) /
            props.duration;

        result.push(
          makeOverviewSection({
            progressRegion: { start: bp.pos / props.duration, end: nextBpPos },
            isActive: isActive(idx + 1),
            idx: idx + 1,
          }),
        );
        return result;
      }, []),
    );
    el.overviewMarkers.replaceChildren(
      ...markers.reduce<Node[]>((result, bp, idx) => {
        const m = asHTML`<div class="overview-marker"></div>`;
        m.style.left = toPercentString(bp.pos, duration);
        m.dataset.idx = String(idx);
        // marker index: section idx -1, or section idx
        if (isActive(idx + 1) || isActive(idx)) {
          m.classList.add("active");
        }
        result.push(m);
        return result;
      }, []),
    );
    el.editorMarkers.replaceChildren(
      ...markers.reduce<HTMLElement[]>((accum, bp, idx) => {
        let result: HTMLElement[] = [];
        // make area before first marker
        if (idx === 0) {
          result.push(
            makeSection(0, bp.pos, duration, 0, isActive(0), () =>
              activateSection(0),
            ),
          );
        }
        const markerEl = markerTemplateEl.cloneNode(true) as HTMLElement;
        markerEl.style.left = toPercentString(bp.pos, duration);
        markerEl.id = `marker-${idx}`;

        function pxToMs(px: number) {
          const width = el.editorMarkers.getBoundingClientRect().width;
          return (px / width) * duration;
        }

        addDragHandlers(
          markerEl,
          el.editorSection,
          (deltaPx: number) => updateMarkerPreview(idx, pxToMs(deltaPx)),
          (deltaPx: number) => finalizeMarker(idx, pxToMs(deltaPx)),
        );

        result.push(markerEl);
        const nextBpPos =
          idx === markers.length - 1 ? duration : markers[idx + 1].pos;
        result.push(
          makeSection(
            bp.pos,
            nextBpPos,
            duration,
            idx + 1,
            isActive(idx + 1),
            () => activateSection(idx + 1),
          ),
        );
        return [...accum, ...result];
      }, []),
    );
  }
  function renderTrackPosition(props: { position: number; duration: number }) {
    d(100, "render track position", { ...props });
    const percentage = (props.position * 100) / props.duration;
    el.playhead.style.transform = `translateX(calc(${percentage}cqw - 1px))`;
  }
  function updateActive(activeSectionIdx: ActiveSection) {
    const overviewMarkers =
      document.querySelectorAll<HTMLElement>(".overview-marker");
    const sections = document.querySelectorAll<HTMLElement>(".between");
    for (let m of overviewMarkers) {
      if (
        includes(Number(m.dataset.idx), {
          start: activeSectionIdx - 1,
          end: activeSectionIdx,
        })
      ) {
        m.classList.add("active");
      } else {
        m.classList.remove("active");
      }
    }
    for (let s of sections) {
      if (s.dataset.idx === String(activeSectionIdx)) {
        s.classList.add("active");
      } else {
        s.classList.remove("active");
      }
    }
  }
  return {
    renderMarkers,
    renderTrackPosition,
    updateActive,
  };
}

function activateMarker(idx: number | null) {
  const markers = document.querySelectorAll(".marker");
  markers.forEach((el) => {
    if (idx !== null && el.id == `marker-${idx}`) {
      el.classList.add("active");
    } else {
      el.classList.remove("active");
    }
  });
}

type TrackIdProps = {
  onSubmit: (trackId: TrackId) => void;
  validate: (trackId: TrackId) => Promise<TrackId | null>;
};
// TODO: use validate function here, and add to props type
function TrackSelect({ onSubmit, validate }: TrackIdProps) {
  el.trackIdInput.pattern =
    ".*(?:\\/|v=|^)([a-zA-Z0-9\\-_]{11})(?:[^a-zA-z0-9\\-_].*)?$";
  el.trackIdInput.addEventListener("keydown", (e) => {
    e.stopPropagation();
  });
  el.trackIdInput.addEventListener("input", (e) => {
    el.trackIdSubmit.disabled = !el.trackIdInput.validity.valid;
  });
  el.trackIdSubmit.addEventListener("click", async () => {
    const trackId = await validate(el.trackIdInput.value);
    d(1, "loading", { trackId, inputVal: el.trackIdInput.value });
    if (trackId) {
      onSubmit(trackId);
    }
  });
}
